import { Badge, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { HELPLINES } from "../content/helplines.js";

export function ResponsibleGaming() {
  return (
    <>
      <Seo
        title="Responsible gaming · Vault Breaker Studios"
        description="Responsible gaming statement for Vault Breaker Studios — a B2B studio. We do not run real-money play; the player-protection hooks we expose to licensed operators are listed here."
        path="/responsible-gaming"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          POLICY
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Responsible gaming
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 780, lineHeight: 1.6 }}>
          Vault Breaker Studios builds games used by licensed operators. <strong>We are
          not an operator and do not directly serve players.</strong> Our <code>/play</code>{" "}
          demo is play-money only — no winnings, no deposits, no real-money risk. Anything
          on this page about cash play, deposit caps, or self-exclusion is describing how
          our games behave on the operator's platform, not on this site.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Operator-facing hooks"
          title="What our games support"
          lead="The operator owns the player relationship; we expose the levers their RG team needs."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {[
            [
              "Configurable bet limits.",
              "Per-operator and per-session minimum and maximum bet are set on the launch token. Bets outside the band are rejected at the RGS before any round opens.",
            ],
            [
              "Time-based session limits.",
              "Operators can set a session time-out window and a reality-check cadence. The RGS issues a soft prompt at each interval and a hard close at the limit.",
            ],
            [
              "Self-exclusion via launch token.",
              "A launch with a self-excluded flag (or a missing flag where the operator's policy mandates it) returns 403 from session/open and round/open immediately. We do not retry, queue, or surface marketing to flagged players.",
            ],
            [
              "Mandatory cool-down after consecutive bust rounds.",
              "After N consecutive bust outcomes (configurable per operator, default disabled until the operator opts in via the MSA), the player is forced into a cool-down before another round can open. The threshold and cool-down duration are part of the operator's RG profile.",
            ],
            [
              "Configurable deposit/loss caps from the operator.",
              "We honour the deposit cap, loss cap, and balance threshold the operator sends on launch. Rounds will not open if a cap would be breached by the next bet.",
            ],
            [
              "Provably-fair seed transparency.",
              "Every round's server seed is committed before play and revealed afterwards. Players, operators, and regulators can reproduce outcomes bit-for-bit from the public engine code.",
            ],
            [
              "No player-facing marketing from us.",
              "Vault Breaker Studios runs no end-user marketing, no acquisition funnels, and no retention emails. The brand sits on the operator's site.",
            ],
          ].map(([title, body], i) => (
            <div
              key={title}
              style={{
                padding: "20px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gap: 6,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
              <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.55 }}>
                {body}
              </p>
            </div>
          ))}
        </Card>
        <p style={{ fontSize: 13, color: "var(--fg-3)", marginTop: 14, lineHeight: 1.55 }}>
          The exact behaviour of each hook — defaults, override mechanics, audit fields —
          is specified in the Master Services Agreement and the technical integration
          guide attached to it.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="If play stops being fun"
          title="Support organisations"
          lead="Independent services. All free. Confidential."
        />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          {HELPLINES.map((h) => (
            <Card key={h.name} style={{ display: "grid", gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 17 }}>{h.name}</h3>
              <span className="label" style={{ color: "var(--fg-3)" }}>
                {h.region}
              </span>
              {h.phone ? (
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--fg-1)" }}>
                  {h.phone}
                </span>
              ) : null}
              <a
                href={h.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--cyan)", fontSize: 13, textDecoration: "none" }}
              >
                Visit {new URL(h.url).hostname} ↗
              </a>
            </Card>
          ))}
        </div>
      </Section>

      <Section tight>
        <p style={{ fontSize: 13, color: "var(--fg-3)", margin: 0 }}>
          Last updated: 2026-05-05.
        </p>
      </Section>
    </>
  );
}
